/**
 * `composeParticipantOpsLogProvenance` — pure shape-transform composer
 * adding a participant-view payload-content slot to each substrate-view
 * provenance entry per Phase 5 §C-iii participant-view decryption seam
 * design memo §1.1 + §4 N+2 ("§C-iii.a IMPL — `composeParticipantOpsLogProvenance`
 * … no `IEnvelopeCryptoAdapter` integration yet — pure shape-transform").
 *
 * # Composition flow
 *
 * 1. Caller fires `composeOpsLogProvenance` upstream → substrate-view
 *    `OpsLogProvenanceComposition` (metadata-only; recording-anchor
 *    provenance categorization)
 * 2. Caller fires per-entry decryption (envelope-crypto adapter via
 *    `<OpsLogContainer />` effect; §C-iii.b sub-PR scope) → produces a
 *    `ReadonlyMap<recordId, ParticipantPayloadStatus>` per-entry result
 *    map; entries without an entry default to `'no-payload'`
 *    substrate-honest framing
 * 3. **This composer batch-applies the per-entry results onto the
 *    substrate-view shape** (preserving all metadata-side fields) and
 *    returns a participant-view shape
 * 4. Consumer (`<OpsLogView />` §C-iii.c sub-PR scope) branches on
 *    `viewMode === 'participant'` and renders `payloadStatus` per
 *    discriminator
 *
 * # Doctrinal anchors (Phase 5 §C-iii design memo §5 binding)
 *
 * 1. **NO SHT BACKDOOR** (Doctrine v1.4 §8 B.1) — composer is pure
 *    shape-transform; cleartext payloads enter ONLY via the
 *    caller-supplied `payloadResults` map produced upstream from
 *    active-session decryption per `feedback_phg_client_active_session_crypto.md`
 *    — no cryptographic operations at this layer
 * 2. **Recovery UX honesty (§8 B.3 ELEVATE)** — `failed` /
 *    `capability-absent` / `no-payload` discriminators preserve
 *    substrate-honest framing at every non-success path; consumer never
 *    sees a "pending" conflation or false-success per Doctrine v1.4
 *    §8 B.3 non-negotiability marker
 * 3. **Pattern B no-parallel-rendering-surface** (`feedback_pattern_b_no_parallel_rendering_surface.md`) —
 *    composer is a client-side pure function; rendering happens at
 *    `<OpsLogView />` per Pattern B doctrinal split
 * 4. **Active-session-only crypto (§6.4)** — composer NEVER touches
 *    `IEnvelopeCryptoAdapter` / `IActiveSession` / DEK / KEK material;
 *    decryption is the container's responsibility (§C-iii.b) and feeds
 *    pre-decrypted results into this composer
 *
 * # Composes with
 *
 * - {@link composeOpsLogProvenance} — substrate-view composer; produces
 *   the input `OpsLogProvenanceComposition` consumed here
 * - `<OpsLogContainer />` §C-iii.b decryption wiring (next sub-PR; sub-PR scope
 *   adds `cryptoAdapter?` + `activeSession?` props + per-entry async
 *   decryption effect that populates the `payloadResults` map)
 * - `<OpsLogView />` §C-iii.c viewMode branch (sub-PR scope after §C-iii.b);
 *   consumes the participant-view shape and renders per `payloadStatus`
 *   discriminator
 *
 * # OQ-orthogonality (Phase 5 §C-iii design memo §3 OQs)
 *
 * This composer is intentionally OQ-1 / OQ-2 / OQ-3 / OQ-4 independent:
 *
 * - **OQ-1** (cryptoAdapter+activeSession required-vs-optional) — props
 *   are container-side concerns; composer accepts a pre-computed result
 *   map regardless of capability shape
 * - **OQ-2** (canonical `payloadAad` field) — payload AAD is consumed at
 *   the §C-iii.b decryption effect, not at this composer; the composer
 *   sees only the decryption outcome
 * - **OQ-3** (i18n migration scope) — composer carries no UI strings;
 *   i18n is a `<OpsLogView />` concern at §C-iii.c
 * - **OQ-4** (test-double posture at envelope-crypto layers) — composer
 *   is pure shape-transform; tests inject `payloadResults` maps directly
 *   without crypto adapter fakes (see binding tests)
 */
/**
 * Compose substrate-view provenance composition with per-entry payload
 * decryption outcomes into participant-view shape. Pure function (no
 * async I/O; no crypto; no React); shape-transform only.
 *
 * Per Phase 5 §C-iii design memo §4 N+2 scope-fence — this is the
 * pure-shape layer; §C-iii.b adds the decryption effect that populates
 * the `payloadResults` map upstream of this composer.
 *
 * Filter-at-composer semantics: entries without a `payloadResults` entry
 * default to `'no-payload'` substrate-honest framing per Doctrine v1.4
 * §8 B.3 non-negotiability marker (never silent-omit; never false-success
 * placeholder).
 *
 * @param substrateView - Upstream `composeOpsLogProvenance` output (metadata-only)
 * @param payloadResults - Per-recordId payload disposition map from §C-iii.b decryption effect; entries without a key default to `'no-payload'`
 */
export function composeParticipantOpsLogProvenance(substrateView, payloadResults) {
    const composedEntries = substrateView.entries.map((e) => {
        const payloadStatus = payloadResults.get(e.entry.recordId) ?? { kind: 'no-payload' };
        // Conditional-spread on optional fields per `feedback_zod_brand_transform_optional_ordering.md`
        // + `feedback_typescript_strict_undefined_guard_test_pattern.md` discipline
        // (exactOptionalPropertyTypes: never assign `{ x: undefined }` to `x?: T`).
        return {
            entry: e.entry,
            provenance: e.provenance,
            ...(e.verifiedKid !== undefined ? { verifiedKid: e.verifiedKid } : {}),
            ...(e.attestedAt !== undefined ? { attestedAt: e.attestedAt } : {}),
            ...(e.failureReason !== undefined ? { failureReason: e.failureReason } : {}),
            ...(e.failureDetail !== undefined ? { failureDetail: e.failureDetail } : {}),
            payloadStatus,
        };
    });
    return { entries: composedEntries };
}
//# sourceMappingURL=compose-participant-ops-log-provenance.js.map